import { removeBackground } from "@imgly/background-removal";
import { ProcessedImageResult } from "./types";

const PASSPORT_SIZE = 600; // 2x2 inches at 300 DPI
const IS_PRODUCTION = import.meta.env.PROD;
const LOCAL_IMGLY_PUBLIC_PATH = "/imgly/";

const getRemovalConfig = (useLocalAssets: boolean) => ({
  ...(useLocalAssets ? { publicPath: LOCAL_IMGLY_PUBLIC_PATH } : {}),
  // Production-safe defaults: CPU avoids WebGPU runtime variance between environments.
  device: "cpu" as const,
  model: "isnet" as const,
  output: {
    format: "image/png" as const,
    quality: 1,
    type: "foreground" as const,
  },
  debug: !IS_PRODUCTION,
});

/**
 * Log helper for error tracking in production
 */
const logError = (step: string, error: any) => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : "";

  console.error(`[PassportPhotoHelper] ${step} - ${errorMessage}`, {
    timestamp: new Date().toISOString(),
    environment: IS_PRODUCTION ? "production" : "development",
    error: error,
    stack: errorStack,
  });
};

/**
 * Log info for debugging
 */
const logInfo = (message: string, data?: any) => {
  if (!IS_PRODUCTION) {
    console.log(`[PassportPhotoHelper] ${message}`, data);
  }
};

/**
 * Converts Blob to HTMLImageElement
 */
const blobToImage = (blob: Blob): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        logInfo("Blob loaded as image", { size: blob.size, type: blob.type });
        resolve(img);
      };

      img.onerror = (event) => {
        const eventType = event instanceof Event ? event.type : "unknown";
        const error = new Error(`Failed to load image from blob: ${eventType}`);
        logError("blobToImage.onerror", error);
        reject(error);
      };

      // Add timeout to catch hanging loads
      const timeout = setTimeout(() => {
        const error = new Error("Image load timeout (blob)");
        logError("blobToImage.timeout", error);
        reject(error);
      }, 10000);

      img.src = url;

      // Clear timeout on load
      img.addEventListener("load", () => clearTimeout(timeout), {
        once: true,
      });
    } catch (error) {
      logError("blobToImage.exception", error);
      reject(error);
    }
  });
};

/**
 * Main reusable helper
 */
export const generatePassportPhoto = async (
  file: File,
): Promise<ProcessedImageResult> => {
  const startTime = performance.now();
  logInfo("Starting passport photo generation", {
    filename: file.name,
    size: file.size,
    type: file.type,
  });

  try {
    // 🔒 Basic validation
    if (!file.type.startsWith("image/")) {
      const error = new Error("Invalid file type. Only images are allowed.");
      logError("Validation", error);
      throw error;
    }

    // STEP 1: Remove background
    logInfo("Step 1: Removing background...");
    let bgRemovedBlob: Blob;

    try {
      // First try local self-hosted assets in production, default package path in development.
      bgRemovedBlob = await removeBackground(
        file,
        getRemovalConfig(IS_PRODUCTION),
      );
      logInfo("Background removed successfully", {
        resultSize: bgRemovedBlob.size,
        resultType: bgRemovedBlob.type,
      });
    } catch (localAssetsError) {
      logError(
        "Step 1: Background removal failed with primary config",
        localAssetsError,
      );

      if (IS_PRODUCTION) {
        // If local assets are unavailable/misconfigured in production, retry with package defaults.
        bgRemovedBlob = await removeBackground(file, getRemovalConfig(false));
        logInfo("Background removed successfully with fallback config", {
          resultSize: bgRemovedBlob.size,
          resultType: bgRemovedBlob.type,
        });
      } else {
        throw localAssetsError;
      }
    }

    // STEP 2: Convert to image
    logInfo("Step 2: Converting blob to image...");
    const subjectImg = await blobToImage(bgRemovedBlob);
    logInfo("Blob converted to image", {
      width: subjectImg.width,
      height: subjectImg.height,
    });

    // STEP 3: Create canvas
    logInfo("Step 3: Creating canvas...");
    const canvas = document.createElement("canvas");
    canvas.width = PASSPORT_SIZE;
    canvas.height = PASSPORT_SIZE;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      const error = new Error("Canvas context not available");
      logError("Step 3: Canvas context", error);
      throw error;
    }

    // STEP 4: Fill RED background
    logInfo("Step 4: Filling red background...");
    ctx.fillStyle = "red";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // STEP 5: Draw image (resize)
    logInfo("Step 5: Drawing image on canvas...");
    try {
      ctx.drawImage(subjectImg, 0, 0, PASSPORT_SIZE, PASSPORT_SIZE);
    } catch (drawError) {
      logError("Step 5: Image drawing failed", drawError);
      throw drawError;
    }

    // STEP 6: Convert to Blob
    logInfo("Step 6: Converting canvas to blob...");
    const blob: Blob = await new Promise((resolve, reject) => {
      try {
        canvas.toBlob(
          (b) => {
            if (!b) {
              const error = new Error("Canvas.toBlob returned null");
              logError("Step 6: toBlob failed", error);
              reject(error);
            } else {
              logInfo("Canvas converted to blob", { size: b.size });
              resolve(b);
            }
          },
          "image/png",
          1,
        );
      } catch (error) {
        logError("Step 6: toBlob exception", error);
        reject(error);
      }
    });

    // STEP 7: Convert to Base64
    logInfo("Step 7: Converting canvas to base64...");
    const base64 = canvas.toDataURL("image/png");

    // STEP 8: Convert to File (reusable)
    logInfo("Step 8: Creating output file...");
    const outputFile = new File([blob], "passport-photo.png", {
      type: "image/png",
    });

    const duration = performance.now() - startTime;
    logInfo("✅ Passport photo generation completed", {
      duration: `${duration.toFixed(2)}ms`,
      fileSize: outputFile.size,
    });

    return {
      blob,
      file: outputFile,
      base64,
      width: PASSPORT_SIZE,
      height: PASSPORT_SIZE,
    };
  } catch (error: any) {
    const duration = performance.now() - startTime;
    const finalError = new Error(
      `Passport photo processing failed: ${error.message}`,
    );
    logError(`Failed after ${duration.toFixed(2)}ms`, finalError);

    // Re-throw with additional context
    console.error("[PassportPhotoHelper] Final Error:", {
      message: error.message,
      stack: error.stack,
      duration: `${duration.toFixed(2)}ms`,
      timestamp: new Date().toISOString(),
    });

    throw finalError;
  }
};
